"use strict";
/**
 * A free tenant's one in-app trial of a paid tier (ARCH-002 issue-#1106
 * amendment).
 *
 * `free` registers without a trial, so an owner who started there could reach a
 * paid tier only by paying for it. This module holds the two decisions the trial
 * turns on, pure so the callable, the scheduler and the plan page ask the same
 * question (GEN-004): may this tenant start the trial, and has its trial lapsed
 * into the fallback to `free`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.tierTrialRefusal = tierTrialRefusal;
exports.decideTrialFallback = decideTrialFallback;
const entitlements_js_1 = require("./entitlements.js");
/**
 * Why the tenant may not start its in-app trial, or `null` when it may.
 *
 * Only a `free`, `active` tenant qualifies. `trialStartedAt` is written when a
 * trial starts and never cleared, so its presence means the tenant has had its
 * trial, on the registration path or this one. The per-identity trial ledger is
 * deliberately not asked: every free registrant's mailbox is already in it. A
 * staff-managed tenant (`billingMode: 'manual'`) is out of reach of every
 * automated lifecycle write, and the fallback is one.
 */
function tierTrialRefusal(candidate) {
    if (candidate.tier !== 'free' || candidate.status !== 'active')
        return 'not-free';
    if (candidate.trialStartedAt !== undefined && candidate.trialStartedAt !== null) {
        return 'trial-used';
    }
    if (candidate.billingMode === 'manual')
        return 'staff-managed';
    return null;
}
/**
 * The fallback a lapsed free-origin trial is due at `now`, or `null` when none
 * is: the trial still runs, it is a paid registrant's trial (no fallback marker,
 * the read-only gate governs it), it has converted (no longer `trialing`), or
 * staff manage the tenant.
 *
 * The fallback puts the tenant back as it stood before the trial. It moves to
 * `free` and `active`, and its feature map is resolved for `free` with the
 * platform overrides and without apps. The apps and the members the trial added
 * are named for the caller to withdraw and deactivate.
 */
function decideTrialFallback(input, now) {
    if (!(0, entitlements_js_1.isFreeOriginTrial)(input))
        return null;
    if (input.billingMode === 'manual')
        return null;
    if (input.trialEndsAt !== null && now.getTime() < input.trialEndsAt.getTime())
        return null;
    return {
        tier: 'free',
        status: 'active',
        features: (0, entitlements_js_1.resolveFeatures)('free', input.overrides, []),
        withdrawnApps: [...input.installedApps],
        deactivate: membersAddedByTrial(input.members, input.trialStartedAt),
    };
}
/**
 * The still-active members enrolled at or after the trial started. `invitedAt`
 * is the durable record of how an account came to exist (ARCH-002 *Invited
 * members* point 4), so a member without it predates the trial or was never
 * invited. Owners are never deactivated.
 */
function membersAddedByTrial(members, trialStartedAt) {
    if (trialStartedAt === null)
        return [];
    return members
        .filter((member) => member.role !== 'owner' &&
        member.state !== 'inactive' &&
        member.invitedAt !== null &&
        member.invitedAt.getTime() >= trialStartedAt.getTime())
        .map((member) => member.uid);
}
//# sourceMappingURL=tier-trial.js.map